<template>
  <section class="ma-4 ma-md-8">
    <t-page-title
      title="Promoters | Ativações"
      :on-report="() => datatable?.openDialogReport()"
    />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :metadata="metadata"
          :report="report"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.order_number }}</span>
          </template>
          <template #[`item.affiliate`]="{ item }">
            <span> {{ item.affiliate?.name }} </span>
          </template>
          <template #[`item.payment_method`]="{ item }">
            <span> {{ item.payment_method?.name }}</span>
          </template>
          <template #[`item.total_value`]="{ item }">
            <span> {{ $formatters.currency(item.total_value) }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, type Ref, useTemplateRef } from 'vue'
import DataTable, { type DataTableType } from '@/components/datatable/DataTable.vue'
import TPageTitle from '@/components/TPageTitle.vue'
import type { AxiosResponse } from 'axios'
import type { DataTableFilter, DataTableHeader, DataTableMeta, DataTableMetaItem } from '@/types/DataTable'
import type PaginatedResponse from '@/types/PaginatedResponse'
import type Order from '@/types/Order'
import AffiliateService from '@/services/AffiliateService'
import ProducerService from '@/services/ProducerService'
import type { Report } from '@/types/Report'
import { currency } from '@/helpers/formatters/currency'
import { useSelectFilter } from '@/helpers/reports/selectFilter'
import { useSnackBar } from '@/stores/snackbar'

const snackbar = useSnackBar()

const datatable = useTemplateRef<DataTableType>('datatable')

const resource = 'affiliates'

const headers: DataTableHeader[] = [
  {
    title: 'Promoter',
    value: 'affiliate',
    key: 'affiliate',
    sortable: false,
  },
  {
    title: 'Evento',
    value: 'event_name',
    key: 'event_name',
    sortable: false,
    align: 'center',
  },
  { title: 'ID Evento', value: 'id', key: 'id', sortable: false },
  {
    title: 'Ingressos Vendidos',
    value: 'total_items',
    key: 'total_items',
    sortable: false,
  },
  {
    title: 'Valor',
    value: 'total_value',
    key: 'total_value',
    sortable: false,
  },
  {
    title: 'Método de Pagamento',
    value: 'payment_method',
    key: 'payment_method',
    sortable: false,
    align: 'center',
  },
]

// The affiliate and event filters start with only a "Todos" (all) option and
// have their real options appended once loadDynamicFilterOptions() resolves,
// mirroring the original filterProvider's behavior. Both share a single
// `ready` promise, matching how the original ran a single Promise.all() for
// both before the first fetch.
const dynamicFiltersReady = Promise.withResolvers<boolean>()

const affiliateItems: Ref<{ title: string, value: string }[]> = ref([ { title: 'Todos', value: '' } ])
const affiliateModel: Ref<string | null> = ref('')

const affiliateFilter: DataTableFilter<string> = {
  model: affiliateModel,
  value: computed(() => affiliateModel.value),
  items: computed(() => affiliateItems.value),
  label: 'Promoter',
  field: 'affiliate',
  icon: 'fa-regular fa-calendar-circle-user',
  type: 'select',
  loading: ref(false),
  ready: dynamicFiltersReady.promise,
  required: false,
}

const eventItems: Ref<{ title: string, value: string }[]> = ref([ { title: 'Todos', value: '' } ])
const eventModel: Ref<string | null> = ref('')

const eventFilter: DataTableFilter<string> = {
  model: eventModel,
  value: computed(() => eventModel.value),
  items: computed(() => eventItems.value),
  label: 'Evento',
  field: 'event_id',
  icon: 'fa-regular fa-calendar',
  type: 'autocomplete',
  loading: ref(false),
  ready: dynamicFiltersReady.promise,
  required: false,
}

const paymentMethodFilter = useSelectFilter({
  label: 'Método de Pagamento',
  field: 'payment_method',
  icon: 'fa-regular fa-money-bill',
  items: [
    { title: 'Todos', value: '' },
    { title: 'Cartão de Crédito', value: '1' },
    { title: 'PIX', value: '5' },
  ],
  required: false,
  initial: '',
})

const filters: Ref<DataTableFilter[]> = ref([affiliateFilter, eventFilter, paymentMethodFilter])

async function loadDynamicFilterOptions() {
  const [events, affiliates] = await Promise.all([
    new ProducerService().events(1, '', 50),
    new AffiliateService().index(1, '', 50),
  ])

  eventItems.value = eventItems.value.concat(events.data.data.map(e => ({
    title: `${e.title} (${e.configuration?.name})`,
    value: e.id,
  })))

  affiliateItems.value = affiliateItems.value.concat(
    affiliates.data.data.map(a => ({ title: a.name, value: a.id })),
  )

  dynamicFiltersReady.resolve(true)
}

loadDynamicFilterOptions()

const metadata: Ref<DataTableMeta> = ref({
  headers: [
    {
      title: 'Ingressos Vendidos',
      value: 'quantity',
      key: 'quantity',
      align: 'center',
      sortable: false,
    },
    {
      title: 'Valor Total',
      value: 'value',
      key: 'value',
      align: 'center',
      sortable: false,
    },
  ],
  items: [] as DataTableMetaItem[],
})

const report: Ref<Report> = ref({
  items: [
    {
      label: 'Código',
      field: 'order_number',
      active: true,
      mandatory: false,
    },
    { label: 'Evento', field: 'event', active: true },
    { label: 'Promoter', field: 'affiliate', active: true },
    { label: 'Valor da Compra', field: 'total_value', active: true },
    {
      label: 'Método de Pagamento',
      field: 'payment_method',
      active: true,
    },
    { label: 'Data da Compra', field: 'created_at', active: true },
  ],
  provider: async (filters: string, selected) => {
    return await new AffiliateService().salesReport(filters, selected)
  },
  filename: 'vendas-comissionadas',
})

async function fetch(page: number, filtersString: string, per_page?: number): Promise<AxiosResponse<PaginatedResponse<Order>>> {
  try {
    const response = await new AffiliateService().sales(page, filtersString, per_page)

    const additional = response.data.additional as { quantity: number, value: number }

    metadata.value.items = [
      { quantity: additional.quantity, value: currency(additional.value) },
    ]

    return response
  } catch (error) {
    snackbar.show('Não foi possível buscar os promoters', 'error')
    throw error
  }
}
</script>
